import React from "react";
import ExcelJS from "exceljs";
import MSWManagementSecondaryCollectionStore from "./store/MSWManagementSecondaryCollectionStore";
import isGHMC, {  formatDateToDDMMYYYY, formattedDate } from "../../../../Common/helper/helpar";

export const MSWManagementVehiclesMonthlySecondaryExport = ({fromDate,toDate}) => {
  const { MSWManagementSecondaryCollectionMonthlyReportData } =
    MSWManagementSecondaryCollectionStore();

  const isProd = isGHMC();

  // Extract dates from the data (same logic as in the table component)
  const extractDates = (data) => {
    if (!data || data.length === 0) return [];

    const dateKeys = Object.keys(data[0]).filter((key) => key.includes("_Deployed"));
    return dateKeys.map((key) => {
      const originalDate = key.split("_")[0]; // e.g., "21 Mar 2025"
      return {
        original: originalDate,
        formatted: formatDateToDDMMYYYY(originalDate), // e.g., "21-03-2025"
      };
    });
  };

  // Get unique dates from the data
  const dates = extractDates(MSWManagementSecondaryCollectionMonthlyReportData);

  // Calculate totals (same logic as in the table component)
  const calculateTotals = (data) => {
    if (!data || data.length === 0) return { allocatedVehicles: 0, dateData: {} };

    const totals = {
      allocatedVehicles: 0,
      dateData: {},
    };

    dates.forEach((date) => {
      totals.dateData[date.original] = {
        deployed: 0,
      };
    });

    data.forEach((row) => {
      totals.allocatedVehicles += row.AllottedVehicleCount || 0;

      dates.forEach((date) => {
        const deployedValue = parseInt(row[`${date.original}_Deployed`] || "0", 10);
        totals.dateData[date.original].deployed += deployedValue;
      });
    });

    return totals;
  };

  const downloadAsExcel = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("MSW Vehicles Monthly Report");

    // Define styles
    const boldFont = { bold: true };
    const headerFill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: isProd ? "F5A9A9" : "F5A9A9" }, // Match table header color
    };
    const headerFont = { bold: true, color: { argb: "FFFFFF" } }; // White text
    const totalFill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: isProd ? "5FA6EB" : "F5A9A9" }, // Match total row color
    };
    const lightRowFill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: isProd ? "AED8FF" : "F8CCCD" }, // Match light row color
    };
    const darkRowFill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: isProd ? "8FC9FF" : "F5A9A9" }, // Match dark row color
    };
    const centered = { horizontal: "center", vertical: "middle" };

    // Get current date and time
    const currentDate = new Date();
    const formattedDateTime = `${currentDate.getDate().toString().padStart(2, "0")}-${(currentDate.getMonth() + 1)
      .toString()
      .padStart(2, "0")}-${currentDate.getFullYear()} ${currentDate.getHours()}:${currentDate
      .getMinutes()
      .toString()
      .padStart(2, "0")}:${currentDate.getSeconds().toString().padStart(2, "0")}`;

    // Add title and timestamp
    worksheet.addRow([
      `Monthly Report - MSW Management Vehicles on the Road - Secondary Collection Report Generated on: ${formattedDateTime}`,
    ]);
    worksheet.mergeCells(1, 1, 1, 4 + dates.length * 2); // Now dates is defined
    worksheet.getCell(1, 1).font = boldFont;
    worksheet.getCell(1, 1).alignment = centered;

    worksheet.addRow(["", "", "Monthly Report - MSW Management Vehicles on the Road - Secondary Collection", ""]);
    worksheet.mergeCells(2, 2, 2, 4 + dates.length * 2);
    worksheet.getCell(2, 2).value = `Monthly Report - MSW Management Vehicles on the Road - Secondary Collection Report as on ${formattedDate(fromDate)} to ${formattedDate(toDate)}`;
    worksheet.getCell(2, 2).font = boldFont;
    worksheet.getCell(2, 12).alignment = { horizontal: "center" };

    // Add empty row for spacing
    worksheet.addRow([]);

    // Define table headers
    const headers = [
      "S.No",
      "Zone",
      "Circle",
      "Allocated Vehicles",
      ...dates.flatMap((date) => [date.formatted, "%"]), // Add date and % columns
    ];
    const headerRow = worksheet.addRow(headers);

    // Style header row
    headerRow.eachCell((cell) => {
      cell.font = headerFont;
      cell.fill = headerFill;
      cell.alignment = centered;
      cell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      };
    });

    // Add data rows
    if (MSWManagementSecondaryCollectionMonthlyReportData && MSWManagementSecondaryCollectionMonthlyReportData.length > 0) {
      MSWManagementSecondaryCollectionMonthlyReportData.forEach((row, index) => {
        const rowData = [
          index + 1,
          row.ZoneName,
          row.CircleName,
          row.AllottedVehicleCount || 0,
          ...dates.flatMap((date) => [
            row[`${date.original}_Deployed`] || "0",
            `${row[`${date.original}_Percentage`] || "0.00"}%`,
          ]),
        ];
        const dataRow = worksheet.addRow(rowData);

        // Style data row (alternate light/dark)
        const fill = index % 2 === 0 ? lightRowFill : darkRowFill;
        dataRow.eachCell((cell) => {
          cell.alignment = centered;
        //   cell.fill = fill;
        //   cell.border = {
        //     top: { style: "thin" },
        //     left: { style: "thin" },
        //     bottom: { style: "thin" },
        //     right: { style: "thin" },
        //   };
        });
      });

      // Add totals row
      const totals = calculateTotals(MSWManagementSecondaryCollectionMonthlyReportData);
      const totalRowData = [
        "Total",
        "",
        "",
        totals.allocatedVehicles,
        ...dates.flatMap((date) => [totals.dateData[date.original]?.deployed || 0, ""]),
      ];
      const totalRow = worksheet.addRow(totalRowData);

      // Style totals row
      totalRow.eachCell((cell) => {
        cell.font = boldFont;
        // cell.fill = totalFill;
        cell.alignment = centered;
        // cell.border = {
        //   top: { style: "thin" },
        //   left: { style: "thin" },
        //   bottom: { style: "thin" },
        //   right: { style: "thin" },
        // };
      });
    } else {
      // Add "No Data Available" row
      const noDataRow = worksheet.addRow(["No Data Available"]);
      worksheet.mergeCells(worksheet.lastRow.number, 1, worksheet.lastRow.number, 4 + dates.length * 2);
      noDataRow.getCell(1).alignment = centered;
      noDataRow.getCell(1).font = boldFont;
      noDataRow.getCell(1).border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      };
    }

    // Set column widths
    worksheet.columns = headers.map(() => ({ width: 15 })); // Adjust width as needed
    worksheet.getColumn(2).width = 25; // Wider column for Zone
    worksheet.getColumn(3).width = 25; // Wider column for Circle

    // Save the workbook
    try {
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "MSW_Vehicles_Monthly_Report.xlsx";
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error generating Excel file:", error);
    }
  };

  return (
    <button className="btn btn-excel" onClick={downloadAsExcel}>
      <i className="fa-solid fa-file-excel me-1 icon-container"></i> Excel
    </button>
  );
};